import type { ComponentProps, HTMLAttributes, ReactNode } from "react";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { Button2 } from "@/ui2/atoms/Button";
import { cn } from "@/lib/helpers/cn";
import EnvelopeIcon from "@/assets/images/icons2/icon--envelope.svg?component";
import { isUserAdmin, isUserPCP } from "@/redux/actions/auth";
import MessagingIcon from "@/assets/images/icons2/icon--messaging.svg?component";
import type { PatientDetails } from "@/core/types";
import PhoneIcon from "@/assets/images/icons2/icon--phone.svg?component";
import { selectProfile } from "@/redux/selectors/authSelectors";

import { OnDemandWelcomeEmailModal } from "./OnDemandWelcomeEmailModal";
import { OtcModal } from "./OtcModal";

type PatientActionsProps = {
  patientDetails: Pick<PatientDetails, "email" | "registration_date">;
};

export function PatientActions({ patientDetails }: PatientActionsProps) {
  const { patientId } = useParams<{ patientId: string }>();
  const history = useHistory();
  const profile = useSelector(selectProfile);

  return (
    <ul className="tw:flex tw:items-baseline tw:gap-4 tw:self-center tw:pr-4">
      {!isUserAdmin() && !isUserPCP() && (
        <li>
          <ActionButton
            label="Message"
            onPress={() => history.push(`/app/messages?memberId=${patientId}`)}
          >
            <MessagingIcon />
          </ActionButton>
        </li>
      )}

      {!patientDetails.registration_date && (
        <li>
          <OnDemandWelcomeEmailModal
            email={patientDetails.email}
            patientId={patientId}
            trigger={
              <ActionButton label={<>Resend reg.&nbsp;email</>}>
                <EnvelopeIcon />
              </ActionButton>
            }
          />
        </li>
      )}

      {profile?.otc_enabled && (
        <li>
          <OtcModal
            patientId={patientId}
            trigger={
              <ActionButton label="Generate code">
                <PhoneIcon />
              </ActionButton>
            }
          />
        </li>
      )}
    </ul>
  );
}

function ActionButton({
  children,
  label,
  ...props
}: ComponentProps<typeof Button2> & { label: ReactNode }) {
  return (
    <Button2
      className="tw:group tw:max-w-18 tw:flex-col tw:gap-1.5"
      rawChildren
      size="xs"
      variant="link"
      {...props}
    >
      <IconContainer>{children}</IconContainer>
      <span className="tw:font-normal tw:whitespace-normal">{label}</span>
    </Button2>
  );
}

function IconContainer(props: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "tw:aspect-square tw:size-10 tw:rounded-full! tw:border tw:border-current tw:p-2",
        "tw:group-hover:bg-fill-teal-secondary",
        "tw:group-hover:*:text-text-white",
        "tw:*:max-w-full",
      )}
      {...props}
    />
  );
}
